import React, { useState, useEffect } from "react";
import { Calendar, ChevronRight, ArrowLeft } from "lucide-react";
import AppointmentBooking from "./AppointmentBooking";

/**
 * Öffentliche Gruppen-Terminbuchung (Issue #108).
 *
 * Zeigt die einer Gruppe zugeordneten Online-Kalender zur Auswahl an. Nach der
 * Wahl eines Kalenders wird der bestehende Einzelkalender-Buchungs-Flow
 * (AppointmentBooking) mit den passenden Parametern eingebettet — inkl.
 * "Zurück zur Übersicht".
 */
const AppointmentGroupBooking = () => {
  // Parameter aus dem Root-Element (iFrame-Einbettung).
  const rootEl = document.getElementById("appointment-group-root");
  const groupId = rootEl?.dataset.groupId || null;
  const userSid = rootEl?.dataset.userSid || null;
  const groupColor = rootEl?.dataset.groupColor || "#3B82F6";
  const fontFamily = rootEl?.dataset.fontFamily || null;
  const fontColor = rootEl?.dataset.fontColor || null;
  const theme = rootEl?.dataset.theme || "light";

  const [group, setGroup] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // iFrame-Resize bei Inhaltswechsel anstoßen (analog AppointmentBooking).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.parentIFrame) {
        window.parentIFrame.size();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [group, calendars, selectedCalendar, loading]);

  useEffect(() => {
    if (groupId && userSid) {
      loadGroup();
    } else {
      setError("Ungültige Parameter");
      setLoading(false);
    }
  }, [groupId, userSid]);

  const loadGroup = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/index.php/customer/getPublicGroup?group_id=${groupId}&userSID=${userSid}`
      );
      const data = await response.json();

      if (data && data.success && data.group) {
        setGroup(data.group);
        const cals = Array.isArray(data.group.calendars)
          ? data.group.calendars
          : [];
        setCalendars(cals);
        // Auto-Skip: Bei genau einem Kalender direkt in dessen Buchungs-Flow.
        if (cals.length === 1) {
          setSelectedCalendar(cals[0]);
        }
      } else {
        setError(data?.error || "Gruppe nicht verfügbar");
      }
    } catch (err) {
      console.error("Fehler beim Laden der Gruppe:", err);
      setError("Gruppe konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  // Bei Auto-Skip (nur 1 Kalender) keinen Zurück-Button anzeigen.
  const autoSkipped = calendars.length === 1;

  const handleBack = () => {
    if (autoSkipped) return;
    setSelectedCalendar(null);
  };

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "300px" }}
      >
        <div className="spinner-border" role="status" style={{ color: groupColor }}>
          <span className="visually-hidden">Lädt...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-0">{error}</p>
      </div>
    );
  }

  // Ein Kalender wurde gewählt → bestehenden Buchungs-Flow einbetten.
  if (selectedCalendar) {
    return (
      <div>
        {!autoSkipped && (
          <button
            type="button"
            onClick={handleBack}
            className="btn btn-link text-decoration-none px-0 mb-2 d-inline-flex align-items-center gap-1"
            style={{ color: fontColor || undefined }}
          >
            <ArrowLeft size={18} />
            Zurück zur Übersicht
          </button>
        )}
        <AppointmentBooking
          calendarId={selectedCalendar.uuid}
          userSid={userSid}
          calendarColor={selectedCalendar.color || groupColor}
          fontFamily={fontFamily}
          fontColor={fontColor}
        />
      </div>
    );
  }

  // Landing: Gruppentitel + Beschreibung + Kalender-Auswahl.
  return (
    <div>
      <div className="embedded-header text-center mb-4">
        <h1 className="h3 fw-bold mb-2">{group?.title}</h1>
        {group?.description && (
          <p className="lead text-muted mb-0" style={{ whiteSpace: "pre-line" }}>
            {group.description}
          </p>
        )}
      </div>

      {calendars.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted mb-0">
            Aktuell sind keine Kalender verfügbar.
          </p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {calendars.map((cal) => (
            <div
              key={cal.uuid}
              onClick={() => setSelectedCalendar(cal)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setSelectedCalendar(cal);
              }}
              className="card border-2"
              style={{ cursor: "pointer" }}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      backgroundColor: `${cal.color || groupColor}20`,
                      width: "48px",
                      height: "48px",
                    }}
                  >
                    <Calendar size={24} style={{ color: cal.color || groupColor }} />
                  </div>
                  <div className="flex-grow-1">
                    <h4 className="h5 fw-semibold mb-1">{cal.title}</h4>
                    {cal.description && (
                      <p className="text-muted mb-0 small">{cal.description}</p>
                    )}
                  </div>
                  <ChevronRight size={20} className="text-muted flex-shrink-0" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentGroupBooking;
